import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const SpeakerAnalyticsPage = () => {
  const { speakerProfile } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    profileViews: 0, totalEvents: 0, upcomingEvents: 0, pastEvents: 0,
    topicsCount: 0, citiesSpoken: [], eventsByYear: []
  });

  useEffect(() => {
    if (speakerProfile) fetchAnalytics();
  }, [speakerProfile]);

  const fetchAnalytics = async () => {
    try {
      const { data: eventData } = await supabase
        .from('event_speakers')
        .select('events (id, title, start_date, status, venues (city, state))')
        .eq('speaker_id', speakerProfile.id);

      const events = (eventData || []).map(e => e.events).filter(Boolean);
      const now = new Date();
      const upcoming = events.filter(e => new Date(e.start_date) >= now);
      const past = events.filter(e => new Date(e.start_date) < now);

      // Cities spoken
      const cities = {};
      events.forEach(e => {
        if (e.venues?.city) {
          const key = `${e.venues.city}, ${e.venues.state}`;
          cities[key] = (cities[key] || 0) + 1;
        }
      });
      const citiesSpoken = Object.entries(cities).map(([city, count]) => ({ city, count })).sort((a, b) => b.count - a.count).slice(0, 5);

      // Events by year
      const years = {};
      events.forEach(e => {
        const year = new Date(e.start_date).getFullYear();
        years[year] = (years[year] || 0) + 1;
      });
      const eventsByYear = Object.entries(years).map(([year, count]) => ({ year, count })).sort((a, b) => a.year - b.year).slice(-5);

      setStats({
        profileViews: speakerProfile.view_count || 0,
        totalEvents: events.length,
        upcomingEvents: upcoming.length,
        pastEvents: past.length,
        topicsCount: speakerProfile.topics?.length || 0,
        citiesSpoken,
        eventsByYear
      });
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>;

  const maxEvents = Math.max(...stats.eventsByYear.map(y => y.count), 1);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Analytics</h1>
        <p className="mt-1 text-sm text-gray-500">Track your speaking engagement metrics.</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { label: 'Profile Views', value: stats.profileViews, color: 'purple', icon: 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' },
          { label: 'Total Events', value: stats.totalEvents, color: 'indigo', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
          { label: 'Upcoming', value: stats.upcomingEvents, color: 'green', icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6' },
          { label: 'Completed', value: stats.pastEvents, color: 'gray', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
          { label: 'Topics', value: stats.topicsCount, color: 'yellow', icon: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z' }
        ].map(stat => (
          <div key={stat.label} className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{stat.label}</p>
                <p className={`text-2xl font-bold mt-1 text-${stat.color}-600`}>{stat.value}</p>
              </div>
              <div className={`p-3 bg-${stat.color}-100 dark:bg-${stat.color}-900/30 rounded-xl`}>
                <svg className={`w-6 h-6 text-${stat.color}-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={stat.icon} /></svg>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Events by Year */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Events by Year</h3>
          {stats.eventsByYear.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No event data yet</p>
          ) : (
            <div className="h-48 flex items-end justify-between gap-2">
              {stats.eventsByYear.map((item, i) => (
                <div key={i} className="flex-1 flex flex-col items-center">
                  <span className="text-xs text-gray-500 mb-1">{item.count}</span>
                  <div className="w-full bg-indigo-500 rounded-t-lg" style={{ height: `${(item.count / maxEvents) * 140}px`, minHeight: '4px' }} />
                  <span className="text-xs text-gray-500 mt-2">{item.year}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top Cities */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top Cities</h3>
          {stats.citiesSpoken.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No location data yet</p>
          ) : (
            <div className="space-y-3">
              {stats.citiesSpoken.map((item, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="flex items-center">
                    <span className="w-6 h-6 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-xs font-bold mr-3">{i + 1}</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{item.city}</span>
                  </div>
                  <span className="text-sm text-gray-500">{item.count} event{item.count > 1 ? 's' : ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tips */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl p-6 border border-indigo-100 dark:border-indigo-800">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Tips to Increase Visibility</h3>
        <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <li className="flex items-start"><svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>Complete your profile to 100% for better search ranking</li>
          <li className="flex items-start"><svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>Add a featured video to showcase your speaking style</li>
          <li className="flex items-start"><svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>Keep your availability status up to date</li>
          <li className="flex items-start"><svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>Add specific speaking topics to help event planners find you</li>
        </ul>
      </div>
    </div>
  );
};

export default SpeakerAnalyticsPage;
